import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { browserLocalPersistence, createUserWithEmailAndPassword, onAuthStateChanged, sendPasswordResetEmail, setPersistence, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { FiLogOut } from 'react-icons/fi';
import { auth, db } from '../firebase';
import { readableError } from './api';
import CommunityWorkspace from './CommunityWorkspace';
import MotionHighlights from './MotionHighlights';
import { analyticsErrorArea, startCommunityAnalytics, trackCommunity, useCommunityPageView } from './analytics';
import './community.css';

export const COMMUNITY_URL = 'https://ting-vision-community-review.web.app';
const actionSettings = { url: COMMUNITY_URL };
const DEMO_ENABLED = process.env.REACT_APP_COMMUNITY_DEMO === 'true';

async function ensureReviewerProfile(user) {
  const ref = doc(db, 'users', user.uid);
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    if (snapshot.exists()) return snapshot.data();
    const profile = { email: user.email || '', role: 'reviewer', clientIds: [], cameraIds: [], locationIds: [], createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
    transaction.set(ref, profile);
    return profile;
  });
}

export function CommunityAuthForm({ initialMode = 'login' }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const changeMode = (value) => { setMode(value); setError(''); setNotice(''); };
  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      if (mode === 'reset') {
        await sendPasswordResetEmail(auth, email.trim(), actionSettings);
        trackCommunity('community_password_reset_requested');
        setNotice('If there’s an account with this email, a password reset link is on its way. Check your inbox and spam folder.');
      } else {
        await setPersistence(auth, browserLocalPersistence);
        if (mode === 'signup') {
          const result = await createUserWithEmailAndPassword(auth, email.trim(), password);
          await ensureReviewerProfile(result.user);
          trackCommunity('sign_up', { method: 'email' });
        } else {
          await signInWithEmailAndPassword(auth, email.trim(), password);
          trackCommunity('login', { method: 'email' });
        }
      }
    } catch (requestError) {
      if (mode === 'reset' && requestError?.code === 'auth/user-not-found') {
        setNotice('If there’s an account with this email, a password reset link is on its way. Check your inbox and spam folder.');
      } else setError(readableError(requestError));
      trackCommunity('community_auth_error', { auth_action: mode, error_code: analyticsErrorArea(requestError, 'auth_error') });
    } finally { setBusy(false); }
  };
  return <main className="cr-auth-layout">
    <section className="cr-auth-intro"><span className="cr-eyebrow">Community check</span><h1>A little help.<br />More wildlife found.</h1><p>Help check short motion clips from our cameras. A few taps can uncover a sighting we might have missed.</p>
      <p className="cr-muted">You don’t need to be an expert. “Not sure” is useful, too.</p>
    </section>
    <section className="cr-auth-card">
      <h2>{mode === 'signup' ? 'Join the community' : mode === 'reset' ? 'Reset your password' : 'Welcome back'}</h2>
      <p>{mode === 'signup' ? 'Create an account to start reviewing.' : mode === 'reset' ? 'We’ll email you a link to choose a new password.' : 'Sign in with your Ting Vision account.'}</p>
      <form onSubmit={submit}>
        <label>Email address<input required type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy} /></label>
        {mode !== 'reset' && <label>Password<input required type="password" minLength={mode === 'signup' ? 6 : undefined} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} value={password} onChange={(event) => setPassword(event.target.value)} disabled={busy} /></label>}
        {mode === 'signup' && <p className="cr-muted cr-small">Use at least 6 characters.</p>}
        {error && <p className="cr-error" role="alert">{error}</p>}
        {notice && <p className="cr-success" role="status">{notice}</p>}
        <button type="submit" className="cr-primary" disabled={busy}>{busy ? 'Please wait…' : mode === 'signup' ? 'Create account' : mode === 'reset' ? 'Send reset link' : 'Sign in'}</button>
      </form>
      {mode === 'login' && <button className="cr-text-button" onClick={() => changeMode('reset')} disabled={busy}>Forgot password?</button>}
      <div className="cr-auth-switch">{mode === 'login' ? <>New here? <button className="cr-text-button" onClick={() => changeMode('signup')} disabled={busy}>Create an account</button></>
        : <button className="cr-text-button" onClick={() => changeMode('login')} disabled={busy}>Back to sign in</button>}</div>
      <p className="cr-small cr-muted">By taking part, you agree to our <a href="https://ting-vision.web.app/privacy-policy" target="_blank" rel="noreferrer">privacy policy</a>.</p>
    </section>
  </main>;
}

// Compile-time opt-in used only by a separate local design-preview build.
function createDemoApi(motion = false) {
  let reviewedCount = 0;
  let completed = false;
  let lastSubmission = null;
  let undoToken = null;
  let undoCount = 0;
  const task = motion ? {
    id: 'motion-design-preview', videoUrl: process.env.REACT_APP_COMMUNITY_DEMO_VIDEO_URL || '/community-motion-demo.mp4', fullClipUrl: process.env.REACT_APP_COMMUNITY_DEMO_FULL_CLIP_URL || null,
    maskVideoUrl: process.env.REACT_APP_COMMUNITY_DEMO_MASK_VIDEO_URL || null,
    frameW: Number(process.env.REACT_APP_COMMUNITY_DEMO_VIDEO_WIDTH) || 640, frameH: Number(process.env.REACT_APP_COMMUNITY_DEMO_VIDEO_HEIGHT) || 360, fps: 25,
    boxes: Array.from({ length: 125 }, (_, frameIndex) => ({ frameIndex, x1: 30 + frameIndex, y1: 50, x2: 85 + frameIndex, y2: 120, detected: frameIndex % 5 !== 0 })),
    cameraLabel: 'South Post', capturedAt: '2026-09-08T10:00:00Z',
  } : {
    id: 'design-preview', imageUrl: process.env.REACT_APP_COMMUNITY_DEMO_IMAGE_URL || '/community-demo.jpg',
    frameW: 1200, frameH: 1080, box: { x1: 130, y1: 515, x2: 320, y2: 595 },
    originalClassId: 0, originalSpecies: 'leopard', cameraLabel: 'South Post', capturedAt: '2026-01-18T11:44:10Z',
  };
  if (motion) {
    const total = Number(process.env.REACT_APP_COMMUNITY_DEMO_MOTION_COUNT) || 140;
    const tasks = Array.from({ length: total }, (_, index) => ({ ...task, id: process.env.REACT_APP_COMMUNITY_DEMO_HEX_IDS === 'true' ? (index + 1).toString(16).padStart(64, '0') : `motion-design-preview-${index + 1}` }));
    const reviews = new Map();
    const tokens = new Map();
    const submittedRequests = new Map();
    const undoneRequests = new Map();
    let restoredId = null;
    const remaining = () => {
      const items = tasks.filter((item) => !reviews.has(item.id));
      return restoredId ? [items.find((item) => item.id === restoredId), ...items.filter((item) => item.id !== restoredId)].filter(Boolean) : items;
    };
    const publicTask = (item) => ({ ...item, ...(tokens.has(item.id) ? { undoToken: tokens.get(item.id) } : {}) });
    const receipt = () => [...reviews.values()].at(-1) || null;
    const fail = (message) => { throw Object.assign(new Error(message), { code: 'functions/failed-precondition' }); };
    const once = (requests, data, work) => {
      if (!data.requestId) fail('The demo request needs an idempotency key.');
      const signature = JSON.stringify(data);
      const previous = requests.get(data.requestId);
      if (previous) {
        if (previous.signature !== signature) fail('This request ID already belongs to a different answer.');
        return previous.result;
      }
      const result = Promise.resolve().then(work);
      requests.set(data.requestId, { signature, result });
      return result;
    };
    const wait = (delay) => delay > 0 ? new Promise((resolve) => window.setTimeout(resolve, delay)) : Promise.resolve();
    return {
      scope: `motion-demo-${Date.now()}`,
      leaderboard: async () => ({ entries: ['Nadav', 'Sarah', 'James', 'Alex', 'Lebo'].map((name, index) => ({ rank: index + 1, name, reviewedCount: 542 - index * 83, isYou: false })) }),
      next: async () => ({ task: remaining()[0] ? publicTask(remaining()[0]) : null, reviewedCount: reviews.size, lastSubmission: receipt() }),
      progress: async () => ({ task: null, reviewedCount: reviews.size, lastSubmission: receipt(), hasMore: false, progressOnly: true }),
      buffer: async ({ limit = 100, excludeTaskIds = [], includeLastTask = false, startup = false } = {}) => {
        if (!startup && limit > 1) await wait(Number(process.env.REACT_APP_COMMUNITY_DEMO_BUFFER_DELAY_MS) || 0);
        const assigned = startup ? remaining().filter((item) => !excludeTaskIds.includes(item.id)).slice(0, 1) : remaining().slice(0, 100);
        const last = !startup && includeLastTask && tasks.find((item) => item.id === receipt()?.taskId);
        return { tasks: assigned.filter((item) => !excludeTaskIds.includes(item.id)).slice(0, limit).map(publicTask), assignedTaskIds: assigned.map((item) => item.id), reviewedCount: reviews.size, lastSubmission: receipt(), hasMore: remaining().length > assigned.length,
          ...(!startup && includeLastTask ? { lastTask: last ? publicTask(last) : null } : {}) };
      },
      submit: (data) => once(submittedRequests, data, async () => {
        const { taskId, requestId, undoToken: replacementToken } = data;
        await wait(Number(process.env.REACT_APP_COMMUNITY_DEMO_SAVE_DELAY_MS) || 0);
        if (!tasks.some((item) => item.id === taskId)) fail('This demo clip does not exist.');
        const requiredToken = tokens.get(taskId);
        if (requiredToken && replacementToken !== requiredToken) fail('The replacement answer needs its current Undo token.');
        if (!requiredToken && replacementToken) fail('This Undo token is no longer active.');
        if (reviews.has(taskId)) return { accepted: false, reason: 'already-reviewed', reviewedCount: reviews.size, lastSubmission: receipt() };
        reviews.set(taskId, { taskId, submissionId: requestId });
        if (restoredId) tokens.delete(restoredId);
        restoredId = null;
        return { accepted: true, reviewedCount: reviews.size, lastSubmission: receipt() };
      }),
      undo: async (data) => {
        const event = await once(undoneRequests, data, async () => {
          await wait(Number(process.env.REACT_APP_COMMUNITY_DEMO_UNDO_DELAY_MS) || 0);
          const latest = receipt();
          if (!latest || latest.taskId !== data.taskId || latest.submissionId !== data.submissionId) fail('Only the latest accepted demo review can be undone.');
          reviews.delete(data.taskId); restoredId = data.taskId;
          const token = `demo-undo-${++undoCount}`;
          tokens.set(data.taskId, token);
          return { taskId: data.taskId, token };
        });
        return { undone: true, reviewedCount: reviews.size, lastSubmission: receipt(), undoToken: event.token,
          undoActive: restoredId === event.taskId && tokens.get(event.taskId) === event.token };
      },
    };
  }
  return {
    next: async () => ({ task: completed ? null : { ...task, ...(undoToken ? { undoToken } : {}) }, reviewedCount, lastSubmission }),
    submit: async ({ requestId }) => { if (!completed) reviewedCount += 1; completed = true; lastSubmission = { taskId: task.id, submissionId: requestId }; return { reviewedCount, accepted: true, lastSubmission }; },
    undo: async () => { if (completed) reviewedCount -= 1; completed = false; lastSubmission = null; undoToken = `demo-undo-${++undoCount}`; return { undone: true, reviewedCount, lastSubmission }; },
  };
}

export default function CommunityApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const view = location.pathname.replace(/\/+$/, '') === '/highlights' ? 'highlights' : 'review';
  const [navigationBusy, setNavigationBusy] = useState(false);
  const [session, setSession] = useState({ user: null, profile: null });
  const [authLoading, setAuthLoading] = useState(!DEMO_ENABLED);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const demoApi = useRef(null);
  if (DEMO_ENABLED && !demoApi.current) demoApi.current = { yolo: createDemoApi(), motion: createDemoApi(true) };
  useEffect(() => {
    startCommunityAnalytics();
    if (DEMO_ENABLED) return undefined;
    let active = true;
    let revision = 0;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const request = ++revision;
      setAuthLoading(true); setError('');
      try {
        const profile = user ? await ensureReviewerProfile(user) : null;
        if (active && request === revision) setSession({ user, profile });
      } catch (requestError) {
        if (active && request === revision) { setSession({ user, profile: null }); setError(readableError(requestError)); }
      } finally { if (active && request === revision) setAuthLoading(false); }
    });
    return () => { active = false; unsubscribe(); };
  }, [reloadKey]);
  useEffect(() => {
    document.title = `${view === 'highlights' ? 'Highlights' : 'Community Review'} | Ting Vision`;
    window.scrollTo?.({top: 0});
  }, [view]);
  useCommunityPageView(view, 'website', Boolean(session.user && !authLoading && !error && !DEMO_ENABLED));
  const logOut = async () => {
    try { trackCommunity('community_session_finished'); await signOut(auth); } catch (requestError) { setError(readableError(requestError)); }
  };
  return <div className="community-app">
    <header className={`cr-header${session.user || DEMO_ENABLED ? ' cr-header--navigation' : ''}`}><div className="cr-brand"><span>Ting Vision</span></div>
      {(session.user || DEMO_ENABLED) && <nav className="cr-header-nav" aria-label="Community navigation">{['review', 'highlights'].map(page => <button key={page} aria-current={view === page ? 'page' : undefined} disabled={navigationBusy} onClick={() => { if (page !== view) navigate(page === 'highlights' ? '/highlights' : '/'); }}>{page === 'review' ? 'Review' : 'Highlights'}</button>)}</nav>}
      <div className="cr-header-actions">
        {session.user && <button className="cr-sign-out" aria-label="Finish for now and sign out" onClick={logOut}><span>Finish for now</span><FiLogOut aria-hidden="true" /></button>}
      </div>
    </header>
    {DEMO_ENABLED ? <CommunityWorkspace yoloApi={demoApi.current.yolo} motionApi={demoApi.current.motion} demo />
      : authLoading ? <main className="cr-empty" role="status">Loading your account…</main>
        : error ? <main className="cr-empty"><h1>We couldn’t load your account</h1><p className="cr-error" role="alert">{error}</p><button className="cr-primary" onClick={() => setReloadKey((value) => value + 1)}>Try again</button></main>
          : !session.user ? <CommunityAuthForm />
            : view === 'highlights' ? <MotionHighlights key={session.user.uid} initialHighlightId={new URLSearchParams(location.search).get('highlight')} /> : <CommunityWorkspace key={session.user.uid} onBusyChange={setNavigationBusy} />}
  </div>;
}
